import {
  FREE_EMAIL_AUDIT_API_PATH,
  type FreeEmailAuditReport,
  type FreeEmailAuditRequest,
} from '@/lib/free-tools/email-audit/contracts'

export type EmailAuditRequestResult =
  | { ok: true; report: FreeEmailAuditReport }
  | { ok: false; message: string }

const NETWORK_FAILURE =
  'The audit could not reach Brew. Check your connection and try again.'
const UNREADABLE_FAILURE = 'The audit did not finish. Try again in a moment.'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/**
 * Read the route's JSON without trusting its shape: a proxy or platform
 * error can answer with any body, and none of them may throw here.
 */
function readResult(payload: unknown): EmailAuditRequestResult {
  if (isRecord(payload) && isRecord(payload.report)) {
    return { ok: true, report: payload.report as FreeEmailAuditReport }
  }
  const message =
    isRecord(payload) && isRecord(payload.error)
      ? payload.error.message
      : undefined
  return {
    ok: false,
    message:
      typeof message === 'string' && message ? message : UNREADABLE_FAILURE,
  }
}

/** POST the free audit from the page. Every failure comes back as a message. */
export async function requestFreeEmailAudit(
  body: FreeEmailAuditRequest
): Promise<EmailAuditRequestResult> {
  let response: Response
  try {
    response = await fetch(FREE_EMAIL_AUDIT_API_PATH, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch {
    return { ok: false, message: NETWORK_FAILURE }
  }
  const payload: unknown = await response.json().catch(() => null)
  return readResult(payload)
}
